/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
// TODO: fix this later
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IArpMedia } from '../../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../../features/arp-registry/ArpRegistry'
import { getMediaTypeFromIndex } from '../../../../api/CoreAPI'
import { keyframes, styled } from '../../../../lib/stitches'
import { useTreeState } from '../../../../stores/Root'
import { ArpMediaType } from '../../../../types/common'
import { SpinnerIcon } from '../../../core/icons'
import { MediaTypes } from '../../../core/Utils'

export const useAsyncEffect = (callback: () => any, deps: any[] = []) => {
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading')
  useEffect(() => {
    setStatus('loading')
    callback()
      .catch((err) => {
        console.warn(err)
        setStatus('error')
      })
      .then(() => setStatus('ready'))
  }, deps)
  return status
}

export const TABS = [MediaTypes.SONGS, MediaTypes.STEMS, MediaTypes.SAMPLES]

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

export const animate = keyframes({
  '0%': { transform: 'translateX(0%)', left: '0%' },
  '20%': { transform: 'translateX(0%)', left: '0%' },
  '80%': { transform: 'translateX(-100%)', left: '100%' },
  '100%': { transform: 'translateX(-100%)', left: '100%' }
})

export const Animated = styled('span', {
  display: 'inline-block',
  position: 'relative',
  whiteSpace: 'nowrap',
  minWidth: '100%',
  overflow: 'visible'
})

export interface TableProps {
  onSelect?: (index: number) => void
  data: IArpMedia[]
  selected: number
  loading?: boolean
  top?: boolean
  onDropdownSelect?: (dropdownValue: string | number) => void
  hideRowPlayControls?: boolean
}

export const useRemix = () => {
  const { studio } = useTreeState()
  const [status, setStatus] = useState<'loading' | 'ready'>('ready')
  const navigate = useNavigate()

  const remixSong = async (index: number) => {
    setStatus('loading')
    await studio.initialize(false)
    studio.reset()
    const song = await ArpRegistry.getByIdAsync(index)
    // segmentTrackRemix(song, 'song')
    studio.song.updateTempo(song.metadata.bpm)
    const remixableMetadata = await ArpRegistry.getByIdsAsync(song.subComponents)
    remixableMetadata.forEach((stem) => studio.addStemFromRemix(stem, false, false))
    studio.song.setSongName(`${song.metadata.title} Remix`)
    navigate('/studio')
    setStatus('ready')
  }

  const remixSample = async (index: number, asClip: boolean = false) => {
    setStatus('loading')
    await studio.initialize(false)
    studio.reset()
    await ArpRegistry.getByIdAsync(index)
    if (asClip) {
      studio.addAudioStem()
      const lastStemIndex = studio.song.stems.length - 1
      studio.addClipFromSampleToAudioStem(index, lastStemIndex, 0)
      studio.setSelectedStemIndex(lastStemIndex)
    } else {
      studio.addNewStemForSample(index)
      const lastStemIndex = studio.song.stems.length - 1
      studio.addNewLoopAndRandomize(lastStemIndex, 0)
      studio.setSelectedStemIndex(lastStemIndex)
    }
    navigate('/studio')
    setStatus('ready')
  }

  const remixStem = async (index: number) => {
    setStatus('loading')
    await studio.initialize(false)
    studio.reset()
    const stem = await ArpRegistry.getByIdAsync(index)
    studio.addStemFromRemix(stem)
    if (stem.metadata.bpm > 0) {
      studio.song.updateTempo(stem.metadata.bpm)
    }
    navigate('/studio')
    setStatus('ready')
  }

  return {
    remixSong,
    remixSample,
    remixStem,
    status
  }
}

const spin = keyframes({
  '0%': { transform: 'rotate(0deg)' },
  '100%': { transform: 'rotate(360deg)' }
})

export const Spinner = styled(SpinnerIcon, {
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  animation: `${spin} 2s linear infinite`
})

export const getMedia = (type: string, index: string): Promise<IArpMedia> => {
  return ArpRegistry.getByIdAsync(index)
}

const COLLABORATORS_CACHE = new Map<string, Collaborator[]>()

export interface Collaborator {
  ethAddress: string
  mediaType: ArpMediaType
  mediaIndex: number
}

export const useCollaborators = (type: ArpMediaType, index: string) => {
  const [collabs, setCollabs] = useState<Collaborator[]>([])
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    if (COLLABORATORS_CACHE.has(`${type}-${index}`)) {
      const collaborators = COLLABORATORS_CACHE.get(`${type}-${index}`)
      setCollabs(collaborators)
      setStatus('ready')
      return
    }

    const setup = async () => {
      const stemIndexes: Set<string> = new Set()
      let subcomponentIndexes: number[] = []
      const artists: Collaborator[] = []

      if (type === MediaTypes.SONGS || type === MediaTypes.SONG) {
        const song = await ArpRegistry.getByIdAsync(index)
        if (!song) return
        console.log('[utilsInspect]', JSON.parse(JSON.stringify(song)))
        artists.push({
          ethAddress: song.user.walletAddress,
          mediaType: ArpMediaType.SONG,
          mediaIndex: song.id
        })
        song.subComponents.forEach((index) => stemIndexes.add(index.toString()))
      } else if (type === MediaTypes.STEMS || type === MediaTypes.STEM) {
        stemIndexes.add(index)
      } else if (type === MediaTypes.SAMPLES || type === MediaTypes.SAMPLE)
        subcomponentIndexes.push(Number(index))
      else {
        throw new Error('no such type')
      }

      const stems = await ArpRegistry.getByIdsAsync(Array.from(stemIndexes))
      stems.forEach((stem) => {
        if (!stem) return
        artists.push({
          ethAddress: stem.user.walletAddress,
          mediaType: ArpMediaType.STEM,
          mediaIndex: stem.id
        })
        stem.subComponents.forEach((stemIndex) => subcomponentIndexes.push(stemIndex))
      })
      subcomponentIndexes = Array.from(new Set(subcomponentIndexes))

      // Dealing with the case where a stem has a stem as a subcomponent
      const subcomponentTypes = await Promise.all(
        subcomponentIndexes.map((id) => getMediaTypeFromIndex(id))
      )
      const subcomponentStems = await ArpRegistry.getByIdsAsync(
        subcomponentIndexes.filter((_, i) => subcomponentTypes[i] === 'stem')
      )
      subcomponentStems.forEach((stem) => {
        if (!stem) return
        artists.push({
          ethAddress: stem.user.walletAddress,
          mediaType: ArpMediaType.STEM,
          mediaIndex: stem.id
        })
      })

      const samples = await ArpRegistry.getByIdsAsync(
        subcomponentIndexes.filter((_, i) => subcomponentTypes[i] === 'sample')
      )
      samples.forEach((sample) => {
        if (!sample) return
        artists.push({
          ethAddress: sample.user.walletAddress,
          mediaType: ArpMediaType.SAMPLE,
          mediaIndex: sample.id
        })
      })

      COLLABORATORS_CACHE.set(`${type}-${index}`, artists)
      setCollabs(artists)
      setStatus('ready')
    }

    setStatus('loading')
    setup().catch((err) => {
      console.warn(err)
      setStatus('error')
    })
  }, [type, index])

  return {
    collabs,
    status,
    uniqueCollabEthAddresses: Array.from(new Set(collabs.map((collab) => collab.ethAddress)))
  }
}

export const SEGMENT_PREVIEW_LOCATIONS = {
  home: 'home song preview',
  explore: 'explore song preview',
  profile: 'profile song preview'
}

export const normalizeSubtypes = (type: string) => {
  if (type === MediaTypes.SAMPLES) return ArpMediaType.SAMPLE
  if (type === MediaTypes.STEMS) return ArpMediaType.STEM
  if (type === MediaTypes.SONGS) return ArpMediaType.SONG

  return type as ArpMediaType
}
